import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listAuditLogsDataTable,
  getAuditLogFilterOptions,
} from '@kirin/api-client/api/audit-logs-table';
import {
  AUDIT_LOGS_FILTERABLE_FIELDS,
  type AuditLog,
  type AuditLogsTableSortField,
} from '@kirin/api-client/contracts/audit-logs-table';
import { apiClient } from '@/lib/api-client';

const DEFAULT_OPTIONS_LIMIT = 20;

/** Fetch audit logs in DataTable format (pagination, filters, sorting, search). */
export async function fetchOrganizationAuditLogs(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<AuditLog>> {
  const response = await listAuditLogsDataTable(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as AuditLogsTableSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );

  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      ...response.meta,
      activeFilters: response.meta.activeFilters ?? [],
    },
  };
}

function buildFilterOptionsQueryKey(
  organizationId: string,
  field: string,
  params: FilterOptionsParams
): readonly unknown[] {
  return [
    'auditLogs',
    'filter-options',
    {
      organizationId,
      field,
      search: params.search,
      limit: params.limit,
      offset: params.offset,
    },
  ];
}

/** Distinct object-type (table_name) options; value === label === raw value. */
export async function fetchObjectTypeFilterOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getAuditLogFilterOptions(apiClient, organizationId, {
    field: AUDIT_LOGS_FILTERABLE_FIELDS.TABLE_NAME,
    search: params.search,
    limit: params.limit ?? DEFAULT_OPTIONS_LIMIT,
    offset: params.offset ?? 0,
  });

  return {
    options: response.options.map(value => ({ value, label: value })),
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

export function getObjectTypeFilterOptionsQueryKey(
  organizationId: string,
  params: FilterOptionsParams
): readonly unknown[] {
  return buildFilterOptionsQueryKey(
    organizationId,
    AUDIT_LOGS_FILTERABLE_FIELDS.TABLE_NAME,
    params
  );
}

/** Distinct audit users; value === user id, metadata carries name/email. */
export async function fetchAuditLogUserFilterOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getAuditLogFilterOptions(apiClient, organizationId, {
    field: AUDIT_LOGS_FILTERABLE_FIELDS.USER,
    search: params.search,
    limit: params.limit ?? DEFAULT_OPTIONS_LIMIT,
    offset: params.offset ?? 0,
  });

  const options = response.optionsWithMetadata
    ? response.optionsWithMetadata.map(option => ({
        value: option.value,
        label: option.label,
        metadata: { name: option.name, email: option.email },
      }))
    : response.options.map(value => ({ value, label: value }));

  return {
    options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

export function getAuditLogUserFilterOptionsQueryKey(
  organizationId: string,
  params: FilterOptionsParams
): readonly unknown[] {
  return buildFilterOptionsQueryKey(
    organizationId,
    AUDIT_LOGS_FILTERABLE_FIELDS.USER,
    params
  );
}

/** Distinct changed `(table, field)` pairs; value === `tableName.field`. */
export async function fetchChangedFieldsFilterOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getAuditLogFilterOptions(apiClient, organizationId, {
    field: AUDIT_LOGS_FILTERABLE_FIELDS.CHANGES,
    search: params.search,
    limit: params.limit ?? DEFAULT_OPTIONS_LIMIT,
    offset: params.offset ?? 0,
  });

  const options = (response.optionsWithMetadata ?? []).map(option => ({
    value: option.value,
    label: option.label,
    metadata: { table: option.table, field: option.field },
  }));

  return {
    options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

export function getChangedFieldsFilterOptionsQueryKey(
  organizationId: string,
  params: FilterOptionsParams
): readonly unknown[] {
  return buildFilterOptionsQueryKey(
    organizationId,
    AUDIT_LOGS_FILTERABLE_FIELDS.CHANGES,
    params
  );
}
